import { useEffect, useState } from 'react';
import { useHttpState } from '@robinpowered/dashboard-apps-common';
import { useAuthContext } from 'contexts';
import { config } from 'config';
import { useFetch } from './useFetch';

/**
 * The Robin REST API data behind the legacy dashboard profile page.
 *
 * Most of the profile page is sourced from GraphQL, but the central `person`
 * object (the "org user") and the manager's display name are only available
 * from the REST core API. The legacy page fetches the org user via
 * `SharedUsersData.getOrganizationUser` and the manager via `GET /users/:id`.
 *
 * This hook ports those REST reads into the shell so the profile page can be
 * recreated in dashboard-apps progressively. The shapes below intentionally
 * mirror the raw REST payload (snake_case) rather than the GraphQL schema.
 */

export type ProfilePageUserEmail = {
  email: string;
  is_verified: boolean;
};

export type ProfilePageUserGroup = {
  id?: number;
  slug: string;
  name: string;
};

export type ProfilePageUserRole = {
  id?: number;
  slug?: string;
  name: string;
};

/** The organization the user belongs to (the REST `account` field). */
export type ProfilePageUserAccount = {
  id: string;
  name: string;
};

export type ProfilePageUser = {
  id: string;
  name: string | null;
  slug?: string;
  avatar?: string | null;
  title?: string | null;
  department?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  manager_id?: number | null;
  time_zone?: string | null;
  primary_email: ProfilePageUserEmail | null;
  account: ProfilePageUserAccount;
  groups: ProfilePageUserGroup[];
  roles: ProfilePageUserRole[];
  /** Map of permission slug (e.g. `places:manage`) to whether the user has it. */
  permissions: Record<string, boolean>;
  user_access: { management_level: number | null } | null;
};

type RobinRestResponse<T> = {
  data: T;
  meta?: Record<string, unknown>;
};

/** The manager's display fields, resolved from `user.manager_id`. */
export type ProfilePageManager = {
  id: number;
  name: string | null;
  avatar: string | null;
  email: string | null;
};

/** Raw REST `GET /users/:id` payload for the manager (snake_case). */
type ProfilePageManagerResponse = {
  id: number;
  name: string | null;
  avatar?: string | null;
  primary_email?: ProfilePageUserEmail | null;
};

/** A location the user administers (the REST `permitted/entities` payload). */
type PermittedEntity = {
  id: string;
  name: string;
};

/**
 * The `include` query param the legacy dashboard sends when resolving the
 * profile page user. `:userId` is a literal token the API resolves to the
 * fetched user. Mirrors `appv2/organization/person/index.js`.
 */
const PROFILE_USER_INCLUDE =
  'groups,roles,permissions:user(:userId):operations(places:manage)';

/** The permission slug controlling office (places) administration. */
const PLACES_MANAGE_PERMISSION = 'places:manage';

export type UseProfilePageUser = {
  user: ProfilePageUser | null;
  /** The manager (avatar, name, email), resolved from `user.manager_id`. */
  manager: ProfilePageManager | null;
  /**
   * Names of the locations the user administers. Empty when the user has the
   * org-wide `places:manage` permission (the Permissions card shows the org
   * name in that case) or when the user administers no specific locations.
   */
  managedLocationNames: string[];
  loading: boolean;
  error: Error | null;
};

/**
 * Fetches the Robin REST API data used by the dashboard profile page for a
 * given user: the org-user object (with groups, roles and the
 * `places:manage` permission) plus the manager's display name.
 *
 * The organization is taken from the active org in `AuthContext`. The query is
 * skipped until both the org and `userId` are available.
 */
export const useProfilePageUser = (
  userId: string | undefined
): UseProfilePageUser => {
  const { get } = useFetch();
  const { currentOrg } = useAuthContext();
  const orgId = currentOrg?.id;

  const {
    data: user,
    loading,
    error,
    setLoading,
    setData,
    setError,
  } = useHttpState<ProfilePageUser>();

  const [manager, setManager] = useState<ProfilePageManager | null>(null);
  const [managedLocationNames, setManagedLocationNames] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (!orgId || !userId) {
      return;
    }

    let cancelled = false;

    const loadProfileUser = async () => {
      setLoading();
      setManager(null);
      setManagedLocationNames([]);

      let fetchedUser: ProfilePageUser;
      try {
        const response = await get<RobinRestResponse<ProfilePageUser>>(
          `${config.apiUrl}/organizations/${orgId}/users/${userId}`,
          { include: PROFILE_USER_INCLUDE },
          { headers: { 'Tenant-Id': orgId } }
        );
        if (cancelled) {
          return;
        }
        fetchedUser = response.data;
        setData(fetchedUser);
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
        }
        return;
      }

      // The manager (avatar, name, email) is a supplementary REST read; a
      // failure here should not surface as a page-level error.
      const managerId = fetchedUser.manager_id;
      if (managerId) {
        try {
          const managerResponse = await get<
            RobinRestResponse<ProfilePageManagerResponse>
          >(`${config.apiUrl}/users/${managerId}`, undefined, {
            headers: { 'Tenant-Id': orgId },
          });
          if (!cancelled && managerResponse.data) {
            const m = managerResponse.data;
            setManager({
              id: m.id,
              name: m.name ?? null,
              avatar: m.avatar ?? null,
              email: m.primary_email?.email ?? null,
            });
          }
        } catch {
          /* manager is optional; ignore */
        }
      }

      // When the user does not have the org-wide places:manage permission,
      // resolve the specific locations they administer. Supplementary read —
      // a failure should not fail the page.
      const hasOrgWideManage =
        fetchedUser.permissions?.[PLACES_MANAGE_PERMISSION] === true;
      if (!hasOrgWideManage) {
        try {
          const entitiesResponse = await get<
            RobinRestResponse<PermittedEntity[]>
          >(
            `${config.apiUrl}/organizations/${orgId}/users/${userId}/permitted/entities`,
            {
              entity_level: 'location',
              permission: PLACES_MANAGE_PERMISSION,
              per_page: '1000',
              page: '1',
            },
            { headers: { 'Tenant-Id': orgId } }
          );
          if (!cancelled && entitiesResponse.data) {
            setManagedLocationNames(
              entitiesResponse.data.map((entity) => entity.name)
            );
          }
        } catch {
          /* managed locations are optional; ignore */
        }
      }
    };

    void loadProfileUser();

    return () => {
      cancelled = true;
    };
  }, [orgId, userId, get, setLoading, setData, setError]);

  return { user, manager, managedLocationNames, loading, error };
};
